import { type HTMLMotionProps, motion, useInView, useReducedMotion } from 'framer-motion';
import { type ReactNode, useRef } from 'react';
import { MOTION_DURATION, MOTION_EASING, VIEWPORT_ONCE } from './motion-presets';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

interface FadeInProps extends HTMLMotionProps<'div'> {
  children: ReactNode;
  delay?: number;
  duration?: number;
  direction?: Direction;
  distance?: number;
  triggerOnce?: boolean;
  blur?: boolean;
  scale?: boolean;
}

function getDirectionalOffset(direction: Direction, distance: number) {
  switch (direction) {
    case 'up':
      return { y: distance };
    case 'down':
      return { y: -distance };
    case 'left':
      return { x: distance };
    case 'right':
      return { x: -distance };
    default:
      return {};
  }
}

export function FadeIn({
  children,
  delay = 0,
  duration = MOTION_DURATION.base,
  direction = 'up',
  distance = 24,
  triggerOnce = true,
  blur = false,
  scale = false,
  ...props
}: FadeInProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const isInView = useInView(ref, triggerOnce ? VIEWPORT_ONCE : undefined);

  const directional = getDirectionalOffset(direction, distance);
  const blurred = blur ? { filter: 'blur(10px)' } : {};
  const unblurred = blur ? { filter: 'blur(0px)' } : {};
  const scaled = scale ? { scale: 0.96 } : {};
  const unscaled = scale ? { scale: 1 } : {};

  const hidden = prefersReducedMotion
    ? { opacity: 1, x: 0, y: 0 }
    : { opacity: 0, ...directional, ...blurred, ...scaled };

  const visible = prefersReducedMotion
    ? { opacity: 1, x: 0, y: 0 }
    : { opacity: 1, x: 0, y: 0, ...unblurred, ...unscaled };

  return (
    <motion.div
      ref={ref}
      initial={hidden}
      animate={isInView || prefersReducedMotion ? visible : hidden}
      transition={{
        duration: prefersReducedMotion ? 0 : duration,
        delay: isInView && !prefersReducedMotion ? delay : 0,
        ease: MOTION_EASING,
      }}
      style={{ willChange: prefersReducedMotion ? 'auto' : 'transform, opacity, filter' }}
      {...props}
    >
      {children}
    </motion.div>
  );
}
